import styled from '@emotion/styled';
import { Avatar, color, typography } from '@uniquegood/realworld-studio-design';
import { Channel } from '../types';
import PlanBadge from './PlanBadge';

interface ChannelItemProps {
  channel: Channel;
  isSelected: boolean;
  onSelect: (channelId: string) => void;
}

const ChannelItem = ({ channel, isSelected, onSelect }: ChannelItemProps) => {
  const isDisabled = !channel.isOwner || channel.plan !== 'Free';

  const handleClick = () => {
    if (!isDisabled) {
      onSelect(channel.id);
    }
  };

  return (
    <Container isSelected={isSelected} isDisabled={isDisabled} onClick={handleClick}>
      <Avatar size="medium" isCreator source={channel.avatarUrl} />

      <ChannelInfo>
        <ChannelName>{channel.name}</ChannelName>
        <ChannelDescription>{channel.description}</ChannelDescription>
      </ChannelInfo>

      {channel.plan !== 'Free' ? (
        <PlanBadge plan={channel.plan} />
      ) : (
        <RadioButton isSelected={isSelected} isDisabled={isDisabled} />
      )}
    </Container>
  );
};

export default ChannelItem;

const Container = styled.div<{ isSelected: boolean; isDisabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${({ isSelected }) => (isSelected ? '#7800ff' : '#e8e8e8')};
  background: ${({ isDisabled }) => (isDisabled ? '#e8e8e8' : '#fafafa')};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;

  &:hover {
    ${({ isDisabled }) =>
      !isDisabled &&
      `
      border-color: #7800ff;
    `}
  }
`;

const ChannelInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChannelName = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChannelDescription = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: ${color.text_default};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RadioButton = styled.div<{ isSelected: boolean; isDisabled: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid ${({ isSelected }) => (isSelected ? '#7800ff' : '#e8e8e8')};
  background: ${({ isSelected }) => (isSelected ? '#7800ff' : 'transparent')};
  position: relative;

  ${({ isSelected }) =>
    isSelected &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #ffffff;
    }
  `}
`;
